import { ClockIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import {
    ArrowsPointingOutIcon,
    PauseIcon,
    PlayIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
} from "@heroicons/react/24/solid";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactPlayer from "react-player";

const RATES = [0.25, 0.5, 1, 1.5, 2];

function fmtTime(secs) {
  if (!Number.isFinite(secs) || secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * VideoPlayer
 *
 * Props:
 *   initialSrc  – optional URL string pre-loaded from match data
 *
 * Layout:
 *   Row 1 (shrink): source input panel — shown by default when no src,
 *                   collapsed to a compact bar when src is set.
 *   Row 2 (flex-1): ReactPlayer filling the remaining space.
 */
const VideoPlayer = forwardRef(function VideoPlayer(
  {
    initialSrc = "",
    onUrlSaved,
    hideSourceControls = false,
    onSync = null,
    syncDisabled = false,
    fitWidth = false,
  },
  ref,
) {
  const [src, setSrc] = useState(initialSrc);
  const [srcLabel, setSrcLabel] = useState(initialSrc);
  const [showPanel, setShowPanel] = useState(!initialSrc);
  const [urlInput, setUrlInput] = useState(initialSrc);
  const [objectUrl, setObjectUrl] = useState(null);
  const fileInputRef = useRef(null);
  const playerRef = useRef(null);
  const videoWrapRef = useRef(null);

  // ── Custom playback state (YouTube/native chrome is disabled) ──────────────
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0); // 0..1 fraction
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => {
      return playerRef.current?.getCurrentTime() || 0;
    },
    seekTo: (seconds) => {
      // A parent seek (e.g. selecting an event) should begin playback; the
      // click-to-play cover clears once playback actually starts (onPlay).
      setPlaying(true);
      playerRef.current?.seekTo(seconds, "seconds");
    },
  }), []);

  // Revoke blob URLs on unmount or when replaced
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  // Sync when the parent swaps initialSrc (e.g. selecting a different match/event)
  useEffect(() => {
    setSrc(initialSrc);
    setSrcLabel(initialSrc);
    setUrlInput(initialSrc);
    setShowPanel(!initialSrc);
    setPlaying(false);
    setPlayed(0);
    setDuration(0);
  }, [initialSrc]);

  const togglePlay = () => {
    setPlaying((p) => !p);
  };

  const cycleRate = () => {
    setRate((r) => {
      const i = RATES.indexOf(r);
      return RATES[(i + 1) % RATES.length];
    });
  };

  const handleSeekChange = (e) => {
    setPlayed(parseFloat(e.target.value));
  };
  const handleSeekStart = () => setSeeking(true);
  const handleSeekCommit = (e) => {
    setSeeking(false);
    const v = parseFloat(e.target.value);
    playerRef.current?.seekTo(v, "fraction");
  };

  const toggleFullscreen = () => {
    const el = videoWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  };

  const applyUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setSrc(trimmed);
    setSrcLabel(trimmed);
    setShowPanel(false);
    onUrlSaved?.(trimmed);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    const newObjectUrl = URL.createObjectURL(file);
    setObjectUrl(newObjectUrl);
    setSrc(newObjectUrl);
    setSrcLabel(file.name);
    setUrlInput("");
    setShowPanel(false);
    // reset so the same file can be re-picked
    e.target.value = "";
  };

  return (
    <div className={`flex flex-col ${fitWidth ? "" : "h-full"}`}>
      {/* ── Compact source bar ──
          The whole source toolbar (label + change button + Sync) is only shown
          when source controls are enabled (the standalone Video Analysis page);
          it is hidden entirely when embedded. */}
      {!hideSourceControls && !showPanel && src && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-base-300 bg-base-100 shrink-0">
          <span className="text-xs text-base-content/60 truncate flex-1" title={srcLabel}>
            {srcLabel}
          </span>
          <button
            className="btn btn-ghost btn-xs"
            title="Change video source"
            onClick={() => setShowPanel(true)}
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
          {onSync && (
            <button
              className="btn btn-ghost btn-xs"
              onClick={onSync}
              disabled={syncDisabled}
              title="Sync event to current video position"
            >
              <ClockIcon className="w-4 h-4" />
              Sync
            </button>
          )}
        </div>
      )}

      {/* ── Source input panel ── */}
      {showPanel && !hideSourceControls && (
        <div className="shrink-0 border-b border-base-300 bg-base-100 px-3 py-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Video Source</span>
            {src && (
              <button
                className="btn btn-ghost btn-xs"
                title="Close"
                onClick={() => setShowPanel(false)}
              >
                ✕
              </button>
            )}
          </div>

          {/* URL row */}
          <div className="flex gap-2">
            <input
              type="text"
              className="input input-bordered input-sm flex-1 text-xs"
              placeholder="Paste URL (http, https, m3u8, mp4…)"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyUrl()}
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={!urlInput.trim()}
              onClick={applyUrl}
            >
              Load
            </button>
          </div>

          {/* Local file row */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-base-content/40">or</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleFile}
            />
            <button
              className="btn btn-outline btn-xs"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose local file…
            </button>
          </div>
        </div>
      )}

      {/* ── Player + custom controls ── */}
      {src ? (
        <div
          ref={videoWrapRef}
          className={`flex flex-col bg-black overflow-hidden ${fitWidth ? "shrink-0" : "flex-1"}`}
        >
          <div className={`relative overflow-hidden ${fitWidth ? "w-full aspect-video" : "flex-1"}`}>
            <ReactPlayer
              ref={playerRef}
              url={src}
              width="100%"
              height="100%"
              controls={false}
              playing={playing}
              muted={muted}
              playbackRate={rate}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onDuration={(d) => setDuration(d)}
              onProgress={({ played: p }) => {
                if (!seeking) setPlayed(p);
              }}
              style={{ position: "absolute", top: 0, left: 0 }}
              config={{
                youtube: {
                  playerVars: {
                    controls: 0,
                    modestbranding: 1,
                    rel: 0,
                    iv_load_policy: 3,
                    showinfo: 0,
                    fs: 0,
                    disablekb: 1,
                    playsinline: 1,
                  },
                },
                file: {
                  attributes: {
                    controlsList: "nodownload",
                    disablePictureInPicture: true,
                  },
                },
              }}
            />

            {/* Transparent tap layer: click the video to toggle play/pause. */}
            <button
              type="button"
              aria-label={playing ? "Pause" : "Play"}
              className="absolute inset-0 w-full h-full"
              onClick={togglePlay}
            />

            {/* Click-to-play cover — shown whenever the video is not playing so
                the provider's paused/poster overlay (title, owner, big centre
                play button) is never visible. */}
            {!playing && (
              <button
                type="button"
                aria-label="Play"
                className="absolute inset-0 w-full h-full flex items-center justify-center bg-black z-10"
                onClick={togglePlay}
              >
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/90 text-primary-content">
                  <PlayIcon className="w-7 h-7 ml-1" />
                </span>
              </button>
            )}
          </div>

          {/* Custom control bar */}
          <div className="shrink-0 flex items-center gap-2 px-2 py-1 bg-base-300 text-base-content">
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              onClick={togglePlay}
              title={playing ? "Pause" : "Play"}
            >
              {playing ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
            </button>

            <span className="text-[11px] tabular-nums text-base-content/70 shrink-0">
              {fmtTime(played * duration)} / {fmtTime(duration)}
            </span>

            <input
              type="range"
              className="range range-primary range-xs flex-1"
              min={0}
              max={0.999999}
              step="any"
              value={played}
              onMouseDown={handleSeekStart}
              onTouchStart={handleSeekStart}
              onChange={handleSeekChange}
              onMouseUp={handleSeekCommit}
              onTouchEnd={handleSeekCommit}
            />

            <button
              type="button"
              className="btn btn-ghost btn-xs w-10 shrink-0 tabular-nums"
              onClick={cycleRate}
              title="Playback speed"
            >
              {rate}x
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? (
                <SpeakerXMarkIcon className="w-4 h-4" />
              ) : (
                <SpeakerWaveIcon className="w-4 h-4" />
              )}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              onClick={toggleFullscreen}
              title="Fullscreen"
            >
              <ArrowsPointingOutIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative flex-1 bg-black overflow-hidden">
          <div className="flex h-full items-center justify-center text-base-content/30 text-sm">
            No video source — enter a URL or choose a local file above
          </div>
        </div>
      )}
    </div>
  );
});

export default VideoPlayer;
